import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Image } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import '../../src/css/Analytics.css';
import usersIcon from '../assets/images/icons/usrIcon.png';
import buildingIcon from '../assets/images/icons/building.png';
import urlIcon from '../assets/images/icons/urlIcon.png';
import apiIcon from '../assets/images/icons/apiIcon.png';
import creditsIcon from '../assets/images/icons/creditsIcon.png';
import companyImage from "../assets/images/icons/CompanyIcon.png";
import calendarIcon from '../assets/images/icons/calendar.png';
import { analyticsService } from '../services/analyticsService';
import { accountService } from '../services/accountService';

const SectionLoader = ({ compact = false }) => (
  <div className={`text-center ${compact ? 'py-1' : 'py-4'}`}>
    <div className={`spinner-border ${compact ? 'spinner-border-sm' : ''} text-primary`} role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const Analytics = ({ selectedCompanyId }) => {
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [companyLogos, setCompanyLogos] = useState({});
  const [sectionLoading, setSectionLoading] = useState({
    users: true,
    accounts: true,
    shortUrls: true,
    monthlyUrls: true,
    apiKeys: true,
    credits: true,
    campaigns: true,
    senderIds: true,
    creditAccounts: true,
    browsers: true,
    devices: true,
    locations: true
  });

  const markSectionLoading = (key, isLoading) => {
    setSectionLoading(prev => ({ ...prev, [key]: isLoading }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.roles[0]);
        setAccountId(selectedCompanyId || decoded.accountId);
      } catch (error) {
        console.error('Error decoding token:', error);
        setError('Error authenticating user');
      }
    } else {
      setError('No authentication token found');
    }
  }, [selectedCompanyId]);


  const [globalStats, setGlobalStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalAccounts: 0,
    activeAccounts: 0,
    totalShortUrls: 0,
    topAccountsByUrls: [],
    topAccountsByCredit: [],
    monthlyShortUrls: [],
    topCampaigns: [],
    topSenderIds: []
  });

  const [accountStats, setAccountStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalShortUrls: 0,
    apiKeyCount: 0,
    creditBalance: 0,
    trackingCredits: 0,
    shorteningCredits: 0,
    topCampaigns: [],
    topSenderIds: [],
    browserDistribution: [],
    locationDistribution: [],
    deviceDistribution: []
  });

  const fetchCompanyLogo = async (fileName) => {
    if (!fileName) {
      return null;
    }
    
    try {
      const blob = await accountService.downloadLogo(fileName);
      
      
      // Check if the response is actually a blob
      if (blob instanceof Blob) {
        
        // Check if blob is empty or has wrong type
        if (blob.size === 0) {
          return null;
        }
        
        // Check if blob type indicates it's actually JSON (error response)
        if (blob.type === 'application/json' || blob.type === 'text/json') {
          console.error('Received JSON response instead of image blob in Analytics - likely an error response');
          // Try to read the blob as text to see the error
          const text = await blob.text();
          console.error('Error response content in Analytics:', text);
          return null;
        }
        
        // Ensure we have a proper MIME type for the blob
        let mimeType = blob.type;
        if (!mimeType || mimeType === 'application/octet-stream' || mimeType === 'application/json') {
          // If no MIME type is detected or it's JSON, assume it's a PNG image
          mimeType = 'image/png';
        }
        
        // Convert blob to base64 data URL with proper MIME type
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            let dataUrl = reader.result;
            
            
            // Ensure the data URL has the correct format: data:image/png;base64,...
            if (dataUrl && !dataUrl.startsWith('data:image/')) {
              // If the data URL doesn't have the proper format, reconstruct it
              const base64Data = dataUrl.split(',')[1] || dataUrl;
              dataUrl = `data:${mimeType};base64,${base64Data}`;
            }
            
            resolve(dataUrl);
          };
          reader.onerror = () => {
            console.error('Error reading blob as data URL in Analytics');
            reject(reader.error);
          };
          reader.readAsDataURL(blob);
        });
      } else {
        console.error('downloadLogo did not return a Blob object in Analytics:', blob);
        return null;
      }
    } catch (error) {
      console.error("Error fetching company logo in Analytics:", error);
      console.error("Error details:", error.response?.data || error.message);
      return null;
    }
  };

  const fetchGlobalActiveUsers = async () => {
    markSectionLoading('users', true);
    try {
      const response = await analyticsService.getGlobalActiveUsers();
      if (response.success) {
        const { totalUsers, activeUsers } = response.data;
        
        setGlobalStats(prev => ({ 
          ...prev, 
          totalUsers: totalUsers,
          activeUsers: activeUsers
        }));
      }
    } catch (err) {
      console.error('Error fetching global active users:', err);
    } finally {
      markSectionLoading('users', false);
    }
  };

  const fetchGlobalActiveAccounts = async () => {
    markSectionLoading('accounts', true);
    try {
      const response = await analyticsService.getGlobalActiveAccounts();
      if (response.success) {
        const { totalAccounts, activeAccounts } = response.data;
        setGlobalStats(prev => ({ 
          ...prev, 
          totalAccounts: totalAccounts || 0,
          activeAccounts: activeAccounts || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching global active accounts:', err);
    } finally {
      markSectionLoading('accounts', false);
    }
  };

  const fetchGlobalTotalShortUrls = async () => {
    markSectionLoading('shortUrls', true);
    try {
      const response = await analyticsService.getGlobalTotalShortUrls();
      if (response.success) {
        setGlobalStats(prev => ({ ...prev, totalShortUrls: response.data }));
      }
    } catch (err) {
      console.error('Error fetching global total short URLs:', err);
    } finally {
      markSectionLoading('shortUrls', false);
    }
  };

  const fetchGlobalTopAccountsByUrls = async () => {
    try {
      const response = await analyticsService.getGlobalTopAccountsByUrls();
      if (response.success) {
        const accountsData = response.data;
        
        const accountDetailsPromises = accountsData.map(async (account) => {
          try {
            const accountResponse = await accountService.getAccountConfigById(account.accountId);
            
            if (accountResponse && accountResponse.logo) {
              const logoUrl = await fetchCompanyLogo(accountResponse.logo);
              return [account.accountId, logoUrl];
            } else {
            }
          } catch (error) {
            console.error(`Error fetching details for account ${account.accountId}:`, error);
          }
          return [account.accountId, null];
        });

        const logos = Object.fromEntries(await Promise.all(accountDetailsPromises));
        setCompanyLogos(prevLogos => {
          const newLogos = { ...prevLogos, ...logos };
          return newLogos;
        });
        setGlobalStats(prev => ({ ...prev, topAccountsByUrls: accountsData }));
      }
    } catch (err) {
      console.error('Error fetching global top accounts by URLs:', err);
    }
  };

  const fetchGlobalTopAccountsByCredit = async () => {
    markSectionLoading('creditAccounts', true);
    try {
      const response = await analyticsService.getGlobalTopAccountsByCredit();
      if (response.success) {
        const accountsData = response.data;
        setGlobalStats(prev => ({ ...prev, topAccountsByCredit: accountsData }));
        markSectionLoading('creditAccounts', false);
        
        const accountDetailsPromises = accountsData.map(async (account) => {
          try {
            const accountResponse = await accountService.getAccountConfigById(account.accountId);
            
            if (accountResponse && accountResponse.logo) {
              const logoUrl = await fetchCompanyLogo(accountResponse.logo);
              return [account.accountId, logoUrl];
            }
          } catch (error) {
            console.error(`Error fetching details for account ${account.accountId}:`, error);
          }
          return [account.accountId, null];
        });

        const logos = Object.fromEntries(await Promise.all(accountDetailsPromises));
        setCompanyLogos(prevLogos => ({ ...prevLogos, ...logos }));
      } else {
        markSectionLoading('creditAccounts', false);
      }
    } catch (err) {
      console.error('Error fetching global top accounts by credit:', err);
      markSectionLoading('creditAccounts', false);
    }
  };

  const fetchGlobalTopCampaigns = async () => {
    markSectionLoading('campaigns', true);
    try {
      const response = await analyticsService.getGlobalTopCampaigns();
      if (response.success) {
        const campaignsData = response.data;
        setGlobalStats(prev => ({ ...prev, topCampaigns: campaignsData }));
        markSectionLoading('campaigns', false);
        
        const uniqueAccountIds = [...new Set(campaignsData.map(campaign => campaign.accountId))];
        
        const accountDetailsPromises = uniqueAccountIds.map(async (accountId) => {
          try {
            const accountResponse = await accountService.getAccountConfigById(accountId);
            
            if (accountResponse) {
              const logoUrl = accountResponse.logo ? 
                await fetchCompanyLogo(accountResponse.logo) : 
                null;
              
              return {
                accountId,
                accountName: accountResponse.accountName,
                logo: logoUrl
              };
            }
          } catch (error) {
            console.error(`Error fetching details for account ${accountId}:`, error);
          }
          return null;
        });

        const accountDetails = (await Promise.all(accountDetailsPromises))
          .filter(detail => detail !== null);
        
        const accountMap = Object.fromEntries(
          accountDetails.map(detail => [detail.accountId, detail])
        );

        const enhancedCampaigns = campaignsData.map(campaign => ({
          ...campaign,
          accountName: accountMap[campaign.accountId]?.accountName || '',
          logo: accountMap[campaign.accountId]?.logo
        }));

        setGlobalStats(prev => ({ ...prev, topCampaigns: enhancedCampaigns }));
      } else {
        markSectionLoading('campaigns', false);
      }
    } catch (err) {
      console.error('Error fetching global top campaigns:', err);
      markSectionLoading('campaigns', false);
    }
  };

  const fetchGlobalTopSenderIds = async () => {
    markSectionLoading('senderIds', true);
    try {
      const response = await analyticsService.getGlobalTopSenderIds();
      if (response.success) {
        const senderIdsData = response.data;
        setGlobalStats(prev => ({ ...prev, topSenderIds: senderIdsData }));
        markSectionLoading('senderIds', false);
        
        const uniqueAccountIds = [...new Set(senderIdsData.map(sender => sender.accountId))];
        
        const accountDetailsPromises = uniqueAccountIds.map(async (accountId) => {
          try {
            const accountResponse = await accountService.getAccountConfigById(accountId);
            
            if (accountResponse) {
              const logoUrl = accountResponse.logo ? 
                await fetchCompanyLogo(accountResponse.logo) : 
                null;
              
              return {
                accountId,
                accountName: accountResponse.accountName,
                logo: logoUrl
              };
            }
          } catch (error) {
            console.error(`Error fetching details for account ${accountId}:`, error);
          }
          return null;
        });

        const accountDetails = (await Promise.all(accountDetailsPromises))
          .filter(detail => detail !== null);
        
        const accountMap = Object.fromEntries(
          accountDetails.map(detail => [detail.accountId, detail])
        );

        const enhancedSenderIds = senderIdsData.map(sender => ({
          ...sender,
          accountName: accountMap[sender.accountId]?.accountName || '',
          logo: accountMap[sender.accountId]?.logo
        }));

        setGlobalStats(prev => ({ ...prev, topSenderIds: enhancedSenderIds }));
      } else {
        markSectionLoading('senderIds', false);
      }
    } catch (err) {
      console.error('Error fetching global top sender IDs:', err);
      markSectionLoading('senderIds', false);
    }
  };

  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const fetchGlobalMonthlyShortUrls = async () => {
    markSectionLoading('monthlyUrls', true);
    try {
      const response = await analyticsService.getGlobalMonthlyShortUrls();
      if (response.success) {
        // Get current month in YYYY-MM format
        const currentDate = new Date();
        const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        setCurrentDate(currentMonth)
        // Find current month's data
        const currentMonthData = response.data.find(item => item.month === currentMonth);
        const currentMonthCount = currentMonthData ? currentMonthData.count : 0;
        
        setGlobalStats(prev => ({ ...prev, monthlyShortUrls: currentMonthCount }));
      }
    } catch (err) {
      console.error('Error fetching global monthly short URLs:', err);
    } finally {
      markSectionLoading('monthlyUrls', false);
    }
  };

  const fetchAccountActiveUsers = async () => {
    markSectionLoading('users', true);
    try {
      const response = await analyticsService.getAccountActiveUsers(accountId);
      if (response.success) {
        const { totalUsers, activeUsers } = response.data;
        setAccountStats(prev => ({ 
          ...prev, 
          totalUsers: totalUsers || 0,
          activeUsers: activeUsers || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching account active users:', err);
    } finally {
      markSectionLoading('users', false);
    }
  };

  const fetchAccountTotalShortUrls = async () => {
    markSectionLoading('shortUrls', true);
    try {
      const response = await analyticsService.getAccountTotalShortUrls(accountId);
      if (response.success) {
        setAccountStats(prev => ({ ...prev, totalShortUrls: response.data }));
      }
    } catch (err) {
      console.error('Error fetching account total short URLs:', err);
    } finally {
      markSectionLoading('shortUrls', false);
    }
  };

  const fetchAccountApiKeys = async () => {
    markSectionLoading('apiKeys', true);
    try {
      const response = await analyticsService.getAccountApiKeysCount(accountId);
      if (response.success) {
        setAccountStats(prev => ({ ...prev, apiKeyCount: response.data }));
      }
    } catch (err) {
      console.error('Error fetching account API keys:', err);
    } finally {
      markSectionLoading('apiKeys', false);
    }
  };

  const fetchAccountCreditBalance = async () => {
    markSectionLoading('credits', true);
    try {
      const response = await analyticsService.getAccountCreditBalance(accountId);
      if (response && response.success) {
        const tracking = Number(response.data?.trackingCredits ?? 0);
        const shortening = Number(response.data?.shorteningCredits ?? 0);
        setAccountStats(prev => ({
          ...prev,
          trackingCredits: Number.isFinite(tracking) ? tracking : 0,
          shorteningCredits: Number.isFinite(shortening) ? shortening : 0
        }));
      } else if (response && response.data && typeof response.data === 'number') {
        // Backward compatibility: treat numeric as shortening credits
        const numeric = Number(response.data);
        setAccountStats(prev => ({
          ...prev,
          shorteningCredits: Number.isFinite(numeric) ? numeric : 0,
          trackingCredits: 0
        }));
      } else if (typeof response === 'number') {
        const numeric = Number(response);
        setAccountStats(prev => ({
          ...prev,
          shorteningCredits: Number.isFinite(numeric) ? numeric : 0,
          trackingCredits: 0
        }));
      }
    } catch (err) {
      console.error('Error fetching account credit balance:', err);
    } finally {
      markSectionLoading('credits', false);
    }
  };

  const fetchAccountTopCampaigns = async () => {
    markSectionLoading('campaigns', true);
    try {
      const response = await analyticsService.getAccountTopCampaigns(accountId);
      if (response.success) {
        const campaignsData = response.data;
        setAccountStats(prev => ({ ...prev, topCampaigns: campaignsData }));
        markSectionLoading('campaigns', false);
        
        try {
          const accountResponse = await accountService.getAccountConfigById(accountId);
          
          if (accountResponse) {
            const logoUrl = accountResponse.logo ? 
              await fetchCompanyLogo(accountResponse.logo) : 
              null;
            
            const enhancedCampaigns = campaignsData.map(campaign => ({
              ...campaign,
              logo: logoUrl
            }));

            setAccountStats(prev => ({ ...prev, topCampaigns: enhancedCampaigns }));
          }
        } catch (error) {
          console.error(`Error fetching account details: ${error}`);
        }
      } else {
        markSectionLoading('campaigns', false);
      }
    } catch (err) {
      console.error('Error fetching account top campaigns:', err);
      markSectionLoading('campaigns', false);
    }
  };

  const fetchAccountTopSenderIds = async () => {
    markSectionLoading('senderIds', true);
    try {
      const response = await analyticsService.getAccountTopSenderIds(accountId);
      if (response.success) {
        const senderIdsData = response.data;
        setAccountStats(prev => ({ ...prev, topSenderIds: senderIdsData }));
        markSectionLoading('senderIds', false);
        
        try {
          const accountResponse = await accountService.getAccountConfigById(accountId);
          
          if (accountResponse) {
            const logoUrl = accountResponse.logo ? 
              await fetchCompanyLogo(accountResponse.logo) : 
              null;
            
            const enhancedSenderIds = senderIdsData.map(sender => ({
              ...sender,
              logo: logoUrl
            }));

            setAccountStats(prev => ({ ...prev, topSenderIds: enhancedSenderIds }));
          }
        } catch (error) {
          console.error(`Error fetching account details: ${error}`);
        }
      } else {
        markSectionLoading('senderIds', false);
      }
    } catch (err) {
      console.error('Error fetching account top sender IDs:', err);
      markSectionLoading('senderIds', false);
    }
  };

  const fetchAccountBrowserDistribution = async () => {
    markSectionLoading('browsers', true);
    try {
      const response = await analyticsService.getAccountBrowserDistribution(accountId);
      if (response.success) {
        setAccountStats(prev => ({ ...prev, browserDistribution: response.data }));
      }
    } catch (err) {
      console.error('Error fetching account browser distribution:', err);
    } finally {
      markSectionLoading('browsers', false);
    }
  };

  const fetchAccountLocationDistribution = async () => {
    markSectionLoading('locations', true);
    try {
      const response = await analyticsService.getAccountLocationDistribution(accountId);
      if (response.success) {
        setAccountStats(prev => ({ ...prev, locationDistribution: response.data }));
      }
    } catch (err) {
      console.error('Error fetching location distribution:', err);
    } finally {
      markSectionLoading('locations', false);
    }
  };

  const fetchAccountDeviceDistribution = async () => {
    markSectionLoading('devices', true);
    try {
      const response = await analyticsService.getAccountDeviceDistribution(accountId);
      if (response.success) {
        setAccountStats(prev => ({ ...prev, deviceDistribution: response.data }));
      }
    } catch (err) {
      console.error('Error fetching device distribution:', err);
    } finally {
      markSectionLoading('devices', false);
    }
  };

  useEffect(() => {
    const fetchAllGlobalStats = () => {
      fetchGlobalActiveUsers();
      fetchGlobalActiveAccounts();
      fetchGlobalTotalShortUrls();
      // fetchGlobalTopAccountsByUrls();
      fetchGlobalTopAccountsByCredit();
      fetchGlobalTopCampaigns();
      fetchGlobalTopSenderIds();
      fetchGlobalMonthlyShortUrls();
    };

    const fetchAllAccountStats = () => {
      fetchAccountActiveUsers();
      fetchAccountTotalShortUrls();
      fetchAccountApiKeys();
      fetchAccountCreditBalance();
      fetchAccountTopCampaigns();
      fetchAccountTopSenderIds();
      fetchAccountBrowserDistribution();
      fetchAccountLocationDistribution();
      fetchAccountDeviceDistribution();
    };

    const fetchData = () => {
      if (!userRole || !accountId) return;

      setError(null);

      if (userRole === 'SUPERADMIN' && !selectedCompanyId) {
        setSectionLoading(prev => ({
          ...prev,
          users: true,
          accounts: true,
          shortUrls: true,
          monthlyUrls: true,
          campaigns: true,
          senderIds: true,
          creditAccounts: true
        }));
        fetchAllGlobalStats();
      } else {
        setSectionLoading(prev => ({
          ...prev,
          users: true,
          shortUrls: true,
          apiKeys: true,
          credits: true,
          campaigns: true,
          senderIds: true,
          browsers: true,
          devices: true,
          locations: true
        }));
        fetchAllAccountStats();
      }
    };

    fetchData();

    const refreshInterval = setInterval(fetchData, 300000);
    return () => clearInterval(refreshInterval);
  }, [userRole, accountId, selectedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
  }, [userRole, accountId, selectedCompanyId]);

  const countryCoordinates = {
    "India": [20.5937, 78.9629],
    "United States": [37.0902, -95.7129],
    "United Kingdom": [55.3781, -3.4359],
  };

  return (
    <Container fluid className="ps-0 pb-4 pe-0 overflow-hidden user-management-container analytics-container" style={{backgroundColor:'#FFFAFA !important'}}>

 <Card className="card-nostyle"  style={{backgroundColor:'#FFFAFA !important'}}>
        <Card.Body className="ps-0 pe-0 pb-0">
          <div className="custom-domain-containers">
            <div
              className="d-flex justify-content-between ps-4 pe-4 align-items-center"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 pb-3 fs-20 poppins fw-600">
                {/* <Image src={creditManagementIcon} className="me-1" width="20" />{" "} */}
                Analytics Dashboard
              </h4>
            </div>
          </div>
        </Card.Body>
      </Card>

<div className="ps-4 pe-4 pt-4">
      {error && (
        <Alert variant="danger" className="mb-4" dismissible>
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      <>
          {userRole === 'SUPERADMIN' && !selectedCompanyId ? (
            <>
              <Row className="g-4 mb-4">
                <Col md={3}>
                  <Card className="h-100 border-0 cus-card">
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className='card-statics-icon'><Image className='w-100' src={usersIcon}/></div>
                      </div>
                      <h6 className="fs-12 fw-normal mb-1">Active / Total Users</h6>
                      {sectionLoading.users ? (
                        <SectionLoader compact />
                      ) : (
                        <h3 className="fs-26 Raleway fw-600 mb-0">
                          {globalStats.activeUsers} / {globalStats.totalUsers}
                        </h3>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 cus-card">
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className='card-statics-icon'><Image className='w-100' src={buildingIcon}/></div>
                      </div>
                      <h6 className="fs-12 fw-normal mb-1">Active / Total Accounts</h6>
                      {sectionLoading.accounts ? (
                        <SectionLoader compact />
                      ) : (
                        <h3 className="fs-26 Raleway fw-600 mb-0">
                          {globalStats.activeAccounts} / {globalStats.totalAccounts}
                        </h3>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 cus-card">
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className='card-statics-icon'><Image className='w-100' src={urlIcon}/></div>
                      </div>
                       <h6 className="fs-12 fw-normal mb-1">Total Short URLs</h6>
                      {sectionLoading.shortUrls ? (
                        <SectionLoader compact />
                      ) : (
                        <h3 className="fs-26 Raleway fw-600 mb-0">{globalStats.totalShortUrls}</h3>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 cus-card">
                    <Card.Body className="d-flex flex-column">
                        <div className="d-flex align-items-center mb-3">
                        <div className='card-statics-icon'><Image className='w-100' src={calendarIcon}/></div>
                      </div>
                        <h6 className="fs-12 fw-normal mb-1">Monthly Shortened URLs</h6>
                      {sectionLoading.monthlyUrls ? (
                        <SectionLoader compact />
                      ) : (
                        <h3 className="fs-26 Raleway fw-600 mb-0 mt-auto">{globalStats.monthlyShortUrls}</h3>
                      )}
                    </Card.Body>
                    <Card.Footer className="bg-white d-flex justify-content-end border-0">
                      <small className="text-muted">Data for: {currentDate}</small>
                      </Card.Footer>
                  </Card>
                </Col>
              </Row>

              <Row className="g-4 mb-4">
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                                        <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0">
                        Top 5 Campaigns
                      </h6>
                      </Card.Header>

                    <Card.Body>
                      {sectionLoading.campaigns ? (
                        <SectionLoader />
                      ) : globalStats.topCampaigns.length > 0 ? (
                        globalStats.topCampaigns.map((campaign, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom:'1px dashed #ddd'}}>
                            <div className="d-flex align-items-center">
                              <Image 
                                src={campaign.logo ? campaign.logo : companyImage}
                                alt="Company Logo"
                                width={25}
                                height={25}
                                className="me-2 rounded-circle img-fluid"
                                style={{border: '1px solid #f4f4f4'}}
                                onLoad={() => {
                                }}
                                onError={(e) => {
                                }}
                              />
                              <span className="fs-12">{campaign.campaignName}</span>
                            </div>
                            <span className="badge bg-success text-dark">{campaign.clickCount} Clicks</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted p-4">No campaigns available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                                                            <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0">
                        Top 5 Sender IDs
                      </h6>
                      </Card.Header>
                    <Card.Body>
                      {sectionLoading.senderIds ? (
                        <SectionLoader />
                      ) : globalStats.topSenderIds.length > 0 ? (
                        globalStats.topSenderIds.map((sender, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom:'1px dashed #ddd'}}>
                            <div className="d-flex align-items-center">
                              <Image 
                                src={sender.logo ? sender.logo : companyImage}
                                alt="Company Logo"
                                width={25}
                                height={25}
                                className="me-2 rounded-circle"
                                style={{border: '1px solid #f4f4f4'}}
                                onLoad={() => {
                                }}
                                onError={(e) => {
                                }}
                              />
                              <span className="fs-12">{sender.senderId}</span>
                            </div>
                            <span className="badge bg-success">{sender.clickCount} Clicks</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted p-4">No sender IDs available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-4 mb-4">
                {/* <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0">
                        Top 5 Accounts by URLs
                      </h6>
                      </Card.Header>
                    <Card.Body>
                      {globalStats.topAccountsByUrls.length > 0 ? (
                        globalStats.topAccountsByUrls.map((account, index) => (
                          <div key={account.accountId} className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom:'1px dashed #ddd'}}>
                            <div className="d-flex align-items-center">
                             
                              <Image 
                                src={companyLogos[account.accountId] || companyImage}
                                alt={account.accountName}
                                width={25}
                                height={25}
                                className="me-2 rounded-circle"
                                style={{border: '1px solid #f4f4f4'}}
                                onLoad={() => {
                                }}
                                onError={(e) => {
                                }}
                              />
                              <span className="fs-12">{account.accountName}</span>
                            </div>
                            <span className="badge position-relative bg-success">{account.count} URLs</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted p-4">No data available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col> */}
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0">
                        Top 5 Accounts by Credit Used
                      </h6>
                      </Card.Header>

                    <Card.Body>
                      {sectionLoading.creditAccounts ? (
                        <SectionLoader />
                      ) : globalStats.topAccountsByCredit.length > 0 ? (
                        globalStats.topAccountsByCredit.map((account, index) => (
                          <div key={account.accountId} className="d-flex justify-content-between align-items-center mb-2 pb-2"  style={{borderBottom:'1px dashed #ddd'}}>
                            <div className="d-flex align-items-center">
                              {/* <span style={{background:'#ebebed', color:'#616161', width: '25px', height: '25px', lineHeight:'25px',padding:'0'}} className="text-black fs-10 badge me-3">{index + 1}</span> */}
                              <Image 
                                src={companyLogos[account.accountId] || companyImage}
                                alt={account.accountName}
                                width={25}
                                height={25}
                                className="me-2 rounded-circle"
                                style={{border: '1px solid #f4f4f4'}}
                                onLoad={() => {
                                }}
                                onError={(e) => {
                                }}
                              />
                              <span className="fs-12">{account.accountName}</span>
                            </div>
                            <span className="badge bg-success">{account.count} Credits</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted p-4">No data available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Row className="g-4 mb-4">
                <Col md={3}>
                  <Card className="h-100 border-0 cus-card">
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className='card-statics-icon'><Image className='w-100' src={usersIcon}/></div>
                      </div>
                      <h6 className="fs-12 fw-normal mb-1">Active / Total Users</h6>
                      {sectionLoading.users ? (
                        <SectionLoader compact />
                      ) : (
                        <h3 className="fs-26 Raleway fw-600 mb-0">
                          {accountStats.activeUsers} / {accountStats.totalUsers}
                        </h3>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 cus-card">
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className='card-statics-icon'><Image className='w-100' src={urlIcon}/></div>
                      </div>
                      <h6 className="fs-12 fw-normal mb-1">Total Short URLs</h6>
                      <div className="">
                        {sectionLoading.shortUrls ? (
                          <SectionLoader compact />
                        ) : (
                          <h3 className="fs-26 Raleway fw-600 mb-0">{accountStats.totalShortUrls}</h3>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 cus-card">
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className='card-statics-icon'><Image className='w-100' src={apiIcon}/></div>
                      </div>
                      <h6 className="fs-12 fw-normal mb-1">API Keys</h6>
                      <div className="">
                        {sectionLoading.apiKeys ? (
                          <SectionLoader compact />
                        ) : (
                          <h3 className="fs-26 Raleway fw-600 mb-0">{accountStats.apiKeyCount}</h3>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="h-100 border-0 cus-card overflow-hidden">
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <div className="card-statics-icon">
                          <Image className="w-100" src={creditsIcon} />
                        </div>
                      </div>

                      <h6 className="fs-12 fw-normal mb-1">Credits Balance</h6>

                      {sectionLoading.credits ? (
                        <SectionLoader compact />
                      ) : (
                        <div className="mt-auto overflow-auto">
                          <div className="d-flex flex-nowrap gap-4">
                            <div className="flex-shrink-0">
                              <span className="fs-22 Raleway fw-600 d-block">
                                {(accountStats.shorteningCredits ?? 0).toLocaleString()}
                              </span>
                              <span className="fs-8 text-muted d-block">Shortening</span>
                            </div>

                            <div className="flex-shrink-0">
                              <span className="fs-22 Raleway fw-600 d-block">
                                {(accountStats.trackingCredits ?? 0).toLocaleString()}
                              </span>
                              <span className="fs-8 text-muted d-block">Tracking</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                </Col>
              </Row>

              <Row className="g-4 mb-4">
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm cus-card overflow-hidden">
                    <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0 text-dark">
                        Top 5 Campaigns
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {sectionLoading.campaigns ? (
                        <SectionLoader />
                      ) : accountStats.topCampaigns.length > 0 ? (
                        accountStats.topCampaigns.map((campaign, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom:'1px dashed #ddd'}}>
                            <div className="d-flex align-items-center">
                              <Image 
                                src={campaign.logo ? campaign.logo : companyImage}
                                alt="Company Logo"
                                width={25}
                                height={25}
                                className="me-2 rounded-circle"
                                style={{border: '1px solid #f4f4f4'}}
                                onLoad={() => {
                                }}
                                onError={(e) => {
                                }}
                              />
                              <span className="fs-12">{campaign.campaignName}</span>
                            </div>
                            <span className="badge bg-success text-dark">{campaign.clickCount} Clicks</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted p-4">No campaigns available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm cus-card overflow-hidden">
                    <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0 text-dark">
                        Top 5 Sender IDs
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {sectionLoading.senderIds ? (
                        <SectionLoader />
                      ) : accountStats.topSenderIds.length > 0 ? (
                        accountStats.topSenderIds.map((sender, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{borderBottom:'1px dashed #ddd'}}>
                            <div className="d-flex align-items-center">
                              <Image 
                                src={sender.logo ? sender.logo : companyImage}
                                alt="Company Logo"
                                width={25}
                                height={25}
                                className="me-2 rounded-circle"
                                style={{border: '1px solid #f4f4f4'}}
                                onLoad={() => {
                                }}
                                onError={(e) => {
                                }}
                              />
                              <span className="fs-12">{sender.senderId}</span>
                            </div>
                            <span className="badge bg-success">{sender.clickCount} Clicks</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted p-4">No sender IDs available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 shadow-sm cus-card overflow-hidden">
                    <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0 text-dark">
                        Browser Distribution
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {sectionLoading.browsers ? (
                        <SectionLoader />
                      ) : accountStats.browserDistribution.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th>Browser</th>
                                <th>Click Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {accountStats.browserDistribution.map((browser, index) => (
                                <tr key={index}>
                                  <td>{browser.browser}</td>
                                  <td>{browser.clickCount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted p-4">No browser distribution data available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 shadow-sm cus-card overflow-hidden">
                    <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0 text-dark">
                        Device Distribution
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {sectionLoading.devices ? (
                        <SectionLoader />
                      ) : accountStats.deviceDistribution.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th>Device Type</th>
                                <th>Click Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {accountStats.deviceDistribution.map((device, index) => (
                                <tr key={index}>
                                  <td>{device.key}</td>
                                  <td>{device.value}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted p-4">No device data available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-4">
                <Col md={12}>
                  <Card className="border-0 shadow-sm cus-card overflow-hidden">
                    <Card.Header className='bg-white pt-3 pb-3 border-0'>
                      <h6 className="fs-14 mb-0 text-dark">
                        Location Distribution
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      {sectionLoading.locations ? (
                        <SectionLoader />
                      ) : accountStats.locationDistribution.length > 0 ? (
                        <>
                          <Row>
                            <Col md={6}>
                              <div style={{ height: "400px", width: "100%" }}>
                                <MapContainer
                                  center={[20, 0]}
                                  zoom={2}
                                  style={{ height: "100%", width: "100%" }}
                                  scrollWheelZoom={false}
                                  attributionControl={false}
                                >
                                  <TileLayer
                                    attribution=""
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                  />
                                  {accountStats.locationDistribution.map((location, index) => {
                                    const coordinates = countryCoordinates[location.key];
                                    if (coordinates) {
                                      return (
                                        <CircleMarker
                                          key={index}
                                          center={coordinates}
                                          radius={Math.min(location.value * 4, 20)}
                                          fillColor="#ff5233"
                                          color="#ff5233"
                                          weight={1}
                                          opacity={0.8}
                                          fillOpacity={0.6}
                                        >
                                          <Popup>
                                            <div className="text-center">
                                              <strong>{location.key}</strong>
                                              <br />
                                              Clicks: {location.value}
                                            </div>
                                          </Popup>
                                        </CircleMarker>
                                      );
                                    }
                                    return null;
                                  })}
                                </MapContainer>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="">
                                <div className="table-responsive">
                                  <table className="table table-sm">
                                    <thead>
                                      <tr>
                                        <th>Country</th>
                                        <th>Clicks</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {accountStats.locationDistribution.map((location, index) => (
                                        <tr key={index}>
                                          <td>{location.key}</td>
                                          <td>{location.value}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </>
                      ) : (
                        <div className="text-center text-muted p-4">No location data available</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
      </>
      </div>
    </Container>
    
  );
};

export default Analytics;